import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import api from "@helpers/api";
import { useCommunity } from "@contexts/CommunityContext";

import {
    clearErrorPath,
    defaultGamification,
    defaultInterval,
    isFilledLink,
    validateConfig,
} from "../utils/validation";

function normalizePlans(availability) {
    if (availability && Array.isArray(availability.plans)) return availability.plans;
    return null;
}

export default function useManageGroup({ modalRef }) {
    const { t } = useTranslation("groupModals");
    const configRef = useRef({});
    const communityRef = useRef({});
    const callbackRef = useRef();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState();
    const [uploadBusyCount, setUploadBusyCount] = useState(0);
    const [errors, setErrors] = useState({});

    const handleUploadBusyChange = useCallback((uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    }, []);

    const { loadCommunities, community } = useCommunity();
    const nav = useNavigate();

    modalRef.current = {
        open(preFill, callback) {
            const base = !preFill.group?.id
                ? { ...community.group.pattern }
                : { ...preFill.group };

            base.plans = normalizePlans(base.availability);

            configRef.current = base;
            communityRef.current = { ...preFill.community };
            callbackRef.current = callback;
            setErrors({});
            setType(configRef.current.type);
            setOpen(true);
        },
    };

    const clearError = useCallback((path) => {
        setErrors((prev) => clearErrorPath(prev, path));
    }, []);

    function buildPayload() {
        const {
            name,
            description,
            banner,
            type,
            awards,
            gamification,
            interactions_enabled,
            links,
            interval,
            plans,
        } = configRef.current;

        const isChallenge = type === "CHALLENGE";
        const cleanLinks = (links || [])
            .filter(isFilledLink)
            .map(({ title, url }) => ({ title: title.trim(), url: url.trim() }));

        return {
            name: name?.trim(),
            description: description?.trim() || null,
            banner: banner || null,
            links: cleanLinks,
            interactions_enabled,
            interval: isChallenge ? interval : null,
            awards: isChallenge ? (awards?.length ? awards : null) : null,
            availability: { plans: plans ?? null },
            gamification: isChallenge ? gamification || null : null,
            type,
        };
    }

    async function handleSubmit() {
        const { isValid, errors: validationErrors } = validateConfig(
            configRef.current,
            t
        );
        setErrors(validationErrors);
        if (!isValid) return;

        try {
            const payload = buildPayload();
            const groupId = configRef.current.id;
            const communityId = parseInt(communityRef.current.id);

            setLoading(true);

            const { data } = groupId
                ? await api.patch(`/community/group/${groupId}`, payload)
                : await api.post(`/community/${communityId}/group`, payload);

            if (data?.id) {
                await callbackRef.current(data);
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);
    }

    async function handleDelete() {
        try {
            await api.delete(`/community/group/${configRef.current.id}`);
        } catch (e) {
            console.log(e);
        }

        nav(-1);
        loadCommunities();
        setOpen(false);
    }

    function handleTypeChange(value) {
        configRef.current.type = value;
        if (value === "CHALLENGE") {
            if (!configRef.current.gamification) {
                configRef.current.gamification = defaultGamification();
            }
            if (
                !configRef.current.interval ||
                typeof configRef.current.interval !== "object" ||
                !configRef.current.interval.start_date ||
                !configRef.current.interval.end_date
            ) {
                configRef.current.interval = defaultInterval();
            }
        }
        setType(value);
    }

    const handleAvailabilityChange = useCallback(
        (result) => {
            configRef.current.plans = result ? [...result] : null;
            setErrors((prev) => clearErrorPath(prev, "plans"));
        },
        []
    );

    return {
        open,
        loading,
        type,
        configRef,
        communityRef,
        uploadBusy: uploadBusyCount > 0,
        errors,
        clearError,
        handleSubmit,
        handleDelete,
        handleClose: () => setOpen(false),
        handleTypeChange,
        handleAvailabilityChange,
        handleUploadBusyChange,
    };
}
